import React from 'react'
import { Tabs } from 'rsuite'
import OverviewImage from '../overview/overviewImage'
import useTranslation from '@/hooks/useTranslation'
import { useVisualTopicSelector } from '.'

/**
 * Reusable View Selector Component
 * Displays navigation tabs for selecting different vehicle views (Front, Rear, Interior, etc.)
 *
 * @param {Object} props
 * @param {string} props.selectedView - Currently selected view
 * @param {Function} props.onSelectView - Callback when view is selected
 * @param {Object} props.hotSpotMapping - Hotspot mapping data
 * @param {string} props.variant - Optional variant passed to the OverviewImage component
 * @param {string} props.className - Optional className for the Tabs component
 * @param {string} [props.additionalImageAltText] - Optional text appended to the generated image alt text
 * @param {...Object} navProps - Additional props to spread onto the underlying Tabs component
 */
const ViewSelector = ({
  selectedView,
  onSelectView,
  hotSpotMapping,
  variant,
  className,
  additionalImageAltText,
  ...navProps
}) => {
  const t = useTranslation()
  const { currentHotspot, onHotSpotClick, getHotspotForCurrentView } = useVisualTopicSelector()
  if (!hotSpotMapping) return null

  return (
    <Tabs
      activeKey={selectedView}
      appearance="subtle"
      onSelect={selectedView => onSelectView({ selectedView })}
      className={className}
      aria-label={t('select_vehicle_view', 'Select vehicle view')}
      aria-describedby="hotspot-help-text"
      {...navProps}
    >
      {Object.keys(hotSpotMapping?.mapping || {}).map(view => {
        const viewTitle = t(`digital_first_view_${view.toLowerCase().replace(/\s+/g, '_')}`)
        let altText = t(`digital_first_view_alt_text_${view.toLowerCase().replace(/\s+/g, '_')}`)
        if (additionalImageAltText) {
          altText = `${altText} ${additionalImageAltText}`
        }
        return (
          <Tabs.Tab key={view} eventKey={view} title={viewTitle}>
            <div>
              {selectedView === view ? (
                <OverviewImage
                  hotspotForCurrentView={getHotspotForCurrentView({ hotSpotMapping, selectedView: view })}
                  selectedPart={currentHotspot}
                  onHotSpotClick={onHotSpotClick}
                  variant={variant}
                  altImage={altText}
                />
              ) : (
                <div>{t('no_image_available', 'No image available')}</div>
              )}
            </div>
          </Tabs.Tab>
        )
      })}
    </Tabs>
  )
}

export default ViewSelector
